// lib/api/axios.ts
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiErrorResponse } from '@/types';

// 커스텀 Axios 인스턴스 타입 정의
interface CustomAxiosInstance extends AxiosInstance {
    get<T = any>(url: string, config?: any): Promise<T>;
    delete<T = any>(url: string, config?: any): Promise<T>;
    post<T = any>(url: string, data?: any, config?: any): Promise<T>;
    put<T = any>(url: string, data?: any, config?: any): Promise<T>;
    patch<T = any>(url: string, data?: any, config?: any): Promise<T>;
}

// Axios 인스턴스 생성
const axiosInstance = axios.create({
    baseURL: import.meta.env.VITE_APP_API_URL, // 개발/프로덕션 모두 실제 URL 사용
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

// 요청 인터셉터
axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // 토큰 추가
        const token = localStorage.getItem('token');
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // 로깅 (개발 환경)
        if (import.meta.env.DEV) {
            console.log('🚀 API Request:', config.method?.toUpperCase(), config.url);
        }

        return config;
    },
    (error: AxiosError) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
    }
);

// 응답 인터셉터
axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        // 로깅 (개발 환경)
        if (import.meta.env.DEV) {
            console.log('✅ API Response:', response.config.url, response.data);
        }

        // blob 타입 응답은 그대로 반환
        if (response.config.responseType === 'blob') {
            return response.data;
        }

        // 일반 응답은 data만 반환
        return response.data;
    },
    (error: AxiosError<ApiErrorResponse>) => {
        // 에러 로깅
        console.error('❌ Response Error:', error);

        // 401 Unauthorized - 토큰 만료
        if (error.response?.status === 401) {
            // 로그인 페이지가 아닌 경우에만 리다이렉트
            if (!window.location.pathname.includes('/login')) {
                localStorage.clear();
                window.location.href = '/login';
            }
        }

        // API 에러 응답 처리
        if (error.response?.data) {
            const apiError = error.response.data;
            const errorMessage = apiError.message || '요청 처리 중 오류가 발생했습니다.';

            // 커스텀 에러 객체 반환
            return Promise.reject({
                code: apiError.code || 'UNKNOWN_ERROR',
                message: errorMessage,
                detail: apiError.detail,
                status: error.response.status,
            });
        }

        // 네트워크 에러 등
        return Promise.reject({
            code: 'NETWORK_ERROR',
            message: '서버와 통신할 수 없습니다. 잠시 후 다시 시도해주세요.',
            detail: error.message,
            status: 0,
        });
    }
);

export default axiosInstance as CustomAxiosInstance;